import { useEffect, useState } from "react";
import type { CSSProperties, ReactElement } from "react";
import type { KundeAkte as KundeAkteDaten, Unternehmen, Zuordnung, Angebot,
              Rechnung } from "@kundeninfo/api-client";
import { Chip, Register, Reiter, Steckbrief } from "@kundeninfo/ui/me-design";
import { markenTextfarben } from "../markenfarben.js";
import { relativeZeit, zuordnungsSatz } from "../format.js";
import { KundeStammdaten } from "./KundeStammdaten.js";
import type { StammdatenUmfeld } from "./KundeStammdaten.js";
import { KundeAnsprechpartner } from "./KundeAnsprechpartner.js";
import type { AnsprechpartnerUmfeld } from "./KundeAnsprechpartner.js";
import { KundeNotizen } from "./KundeNotizen.js";
import type { NotizenUmfeld } from "./KundeNotizen.js";
import { KundeDateien } from "./KundeDateien.js";
import type { DateienClient } from "./KundeDateien.js";
import { KundeAngebote } from "./KundeAngebote.js";
import type { AngeboteClient } from "./KundeAngebote.js";
import { KundeAuftraege } from "./KundeAuftraege.js";
import type { AuftraegeClient, Vorbelegung } from "./KundeAuftraege.js";
import { KundeRechnungen } from "./KundeRechnungen.js";
import type { RechnungenClient } from "./KundeRechnungen.js";
import { KundeWichtiges } from "./KundeWichtiges.js";
import { KundeMonitorKarten } from "./KundeMonitorKarten.js";
import type { MonitorKartenClient } from "./KundeMonitorKarten.js";
import { AdressListe } from "./AdressListe.js";
import type { AdressClient } from "./AdressListe.js";
import { KundeKonzern } from "./KundeKonzern.js";
import type { KonzernClient } from "./KundeKonzern.js";
import type { WichtigesUmfeld } from "./KundeWichtiges.js";
import "./KundenAkte.css";

export type AktenTabId =
  | "uebersicht" | "stammdaten" | "adressen" | "konzern" | "ansprechpartner" | "angebote"
  | "auftraege" | "rechnungen" | "wichtiges" | "notizen" | "dateien";

/** Die Tab-Leiste ist die beständige Navigationsebene innerhalb eines Kunden.
 *  Sie steht vollständig da, auch wo der Inhalt erst in einem späteren Paket kommt. */
export const AKTEN_TABS: { id: AktenTabId; label: string; paket: number }[] = [
  { id: "uebersicht",      label: "Übersicht",       paket: 1 },
  { id: "stammdaten",      label: "Stammdaten",      paket: 1 },
  // Der Reiter steht NEBEN den Stammdaten und nicht in ihnen: Adressen sind eigene Sätze,
  // die angelegt und archiviert werden — sie hängen an keinem Speichern-Knopf, und ein
  // Abschnitt im Stammdaten-Formular hätte zwei Speicherbegriffe auf einem Bildschirm.
  { id: "adressen",        label: "Adressen",        paket: 5 },
  // Direkt hinter den Adressen und vor den Ansprechpartnern: Beide sagen, WER der Kunde
  // ist — wo er sitzt und wo er hingehört —, während alles danach sagt, was mit ihm
  // geschieht. Der Router braucht dafür keine Zeile: `istAktenTab` fragt diese Liste.
  { id: "konzern",         label: "Konzern",         paket: 5 },
  { id: "ansprechpartner", label: "Ansprechpartner", paket: 2 },
  { id: "angebote",        label: "Angebote",        paket: 3 },
  { id: "auftraege",       label: "Aufträge",        paket: 3 },
  { id: "rechnungen",      label: "Rechnungen",      paket: 3 },
  { id: "wichtiges",       label: "Wichtiges",       paket: 3 },
  { id: "notizen",         label: "Notizen",         paket: 4 },
  { id: "dateien",         label: "Dateien",         paket: 4 },
];

export type KundenAkteProps = {
  kunde: KundeAkteDaten;
  activeTab: AktenTabId;
  onTab: (id: AktenTabId) => void;
  /** Für die Farbe der Kürzel-Chips im Kopf. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
  /**
   * Das Arbeitsunternehmen (ME-Etappe 1, Aufgabe 6). Ist der Kunde bei ihm NICHT Kunde,
   * steht am Namen je Kundenbeziehung ein Kürzel-Chip — man sieht sofort, dass man in der
   * Akte eines anderen Unternehmens ist. Ohne Angabe (oder ohne passendes Unternehmen in
   * `companies`) steht dort nichts.
   */
  arbeitsunternehmenId?: string | null;
  /** Die Verdrahtung für den bearbeitbaren Stammdaten-Tab (Aufgabe 16): Klient,
   *  Übernahmerecht, Aktualisierungs-Rückruf und Verlassen-Wächter aus App. */
  stammdaten: StammdatenUmfeld;
  /** Dasselbe für den Ansprechpartner-Tab (Paket 2b, Aufgabe 9) — ohne
   *  `onAktualisiert`: Ansprechpartner ändern den Kundensatz im Kopf nicht. */
  ansprechpartner: AnsprechpartnerUmfeld;
  /**
   * Der Adressen-Reiter (Adressen Etappe 1, Aufgabe 8): der Klient und der Ruf „hol die
   * Akte neu". `ownerId` reicht die Akte aus `kunde.id` nach.
   *
   * `onNeuLaden` und nicht `onAktualisiert(kunde)`: Die Hauptanschrift im Kopf ist
   * ABGELEITET (sie entsteht aus der Hauptadresse, siehe adressAbleitung.ts) — die Akte
   * kennt den neuen Stand also gar nicht und kann ihn nicht nach oben reichen. Denselben
   * Kundensatz noch einmal hinaufzugeben wäre sogar wirkungslos: React verwirft ein
   * `setState` mit DERSELBEN Referenz (Object.is), und der Kopf bliebe alt stehen. Also
   * ein Zähler in App, der den Ladeeffekt der Akte erneut anwirft — dieselbe Naht wie
   * `listeVersion` für die Kundenliste.
   */
  adressen: { client: AdressClient; onNeuLaden: () => void };
  /**
   * Der Konzern-Reiter (Adressen Etappe 2, Aufgabe 5): der Klient und der Weg für den
   * frischen Kundensatz nach einer Eltern-Änderung.
   *
   * Hier `onAktualisiert(akte)` und nicht `onNeuLaden()` wie bei den Adressen: Der
   * Schreibweg antwortet mit der ganzen Akte (neue `version`, neuer `parent`), und ein
   * NEUER Satz ist auch eine neue Referenz — React verwirft ihn also nicht (der Grund,
   * warum der Adressen-Reiter einen Zähler braucht, trifft hier nicht zu). Was darüber
   * hinaus geschieht — etwa die Kundenliste neu zu laden, damit dort „Tochter von" steht
   * — entscheidet der Aufrufer in App.tsx, nicht dieser Reiter.
   */
  konzern: { client: KonzernClient; onAktualisiert: (kunde: KundeAkteDaten) => void };
  /**
   * Sprung in die Akte eines ANDEREN Kunden — aus dem Verweis im Kopf, aus der Elternkette
   * und aus den Töchtern des Konzern-Reiters.
   *
   * Er läuft über den ROUTER (App.tsx) und nicht über einen eigenen Zustand: Der Kunde ist
   * eine Ebene, kein Ziel — und die Zurück-Taste soll zurückführen, wo sie herkam.
   */
  onKunde: (id: string) => void;
  /** Der Notiz-Tab (Paket 4, Aufgabe 5): die Notizmethoden des Klienten, die Id des
   *  angemeldeten Benutzers und ob ADMIN — beides zusammen entscheidet, wer eine Notiz
   *  bearbeiten/löschen darf. `kundeId` reicht die Akte selbst aus `kunde.id` nach. */
  notizen: NotizenUmfeld;
  /** Der Dateien-Tab (Paket 4, Aufgabe 10): die Datei-/Ordner-/fileType-Methoden des
   *  Klienten. `kundeId` reicht die Akte aus `kunde.id` nach. */
  dateien: { client: DateienClient };
  /** Der Angebote-Tab (Paket 3a, Aufgabe 4): nur die Angebotsmethoden des Klienten. Die
   *  wählbaren `companies` (aus `kunde.relations` gefiltert) und die Angebot→Auftrag-Naht
   *  ergänzt die Akte selbst — die App reicht bloss den Klienten durch. */
  angebote: { client: AngeboteClient };
  /** Der Aufträge-Tab (Paket 3a, Aufgabe 7): nur die Auftragsmethoden. `companies` und die
   *  `vorbelegung` aus einem angenommenen Angebot ergänzt die Akte selbst. */
  auftraege: {
    client: AuftraegeClient;
    /** Ist die Sitzung ADMIN? Für das Lösen von Abrechnungsquellen an nicht aktiven
     *  Aufträgen (Entscheid Dirk 29.09.2026, Punkt 7). Weglassbar — dann nicht. */
    istAdmin?: boolean | undefined;
  };
  /** Der Rechnungen-Tab (Paket 3b-2, Aufgabe 4): nur die Rechnungsmethoden. `companies` und
   *  die aus dem Auftrag abgeleitete Rechnung ergänzt die Akte selbst. */
  rechnungen: { client: RechnungenClient };
  /** Der Wichtiges-Tab (Paket 3a, Aufgabe 8): die Hinweis-/Laufzeitwarnungs-Methoden.
   *  `kundeId` reicht die Akte aus `kunde.id` nach. */
  wichtiges: WichtigesUmfeld;
  /**
   * Die MONITOR-KARTEN der Übersicht (Spec „Monitor-Mandantensicht", 08.09.2026): nur die
   * beiden Klientenwege, die sie braucht. `kundeId` reicht die Akte aus `kunde.id` nach.
   *
   * Der Abschnitt liegt in der ÜBERSICHT und nicht in den Stammdaten, wo die Zuordnung
   * gepflegt wird: Er ist eine Auskunft für den ersten Blick auf einen Kunden, keine
   * Stammdatenpflege. Steht kein Mandant zu, zeichnet er gar nichts.
   */
  monitor: { client: MonitorKartenClient };
  /**
   * Der Vorgang, den ein Sprung aus einer GLOBALEN Liste (Menüpunkte „Angebote"/„Aufträge")
   * aufschlagen soll. Er kommt am Navigationsziel mit (`Route.vorgangId`, siehe router.ts)
   * und wird an den Reiter durchgereicht, der gerade dran ist — die Kennung gehört immer
   * zur Vorgangsart des Reiters, weil beide aus demselben Klick stammen. Ein Reiter, der
   * eine ihm unbekannte Kennung bekommt, tut nichts.
   */
  oeffneVorgangId?: string;
};

function chipStil(colorHex: string | undefined): CSSProperties | undefined {
  if (colorHex === undefined) return undefined;
  const { hell, dunkel } = markenTextfarben(colorHex);
  return {
    "--ka-chip-bg": `color-mix(in srgb, ${colorHex} 14%, var(--flaeche))`,
    "--ka-chip-rand": `color-mix(in srgb, ${colorHex} 45%, transparent)`,
    "--ka-chip-text-hell": hell,
    "--ka-chip-text-dunkel": dunkel,
  } as CSSProperties;
}

/** Kürzel + Kundennummer je Unternehmensbeziehung — seit ME-Etappe 2 (Aufgabe 4b) ein
 *  `me-chip` (nicht entfernbar). Die Unternehmensfarbe kommt wie bisher aus `colorHex` über
 *  die `--ka-chip-*`-Eigenschaften am Host; Fläche und Rand des Chips tönt KundenAkte.css
 *  über `::part(chip)`, das Kürzel färbt `.ka-chip__code`. */
function KundenChip({ r, colorHex }: { r: Zuordnung; colorHex: string | undefined }): ReactElement {
  const stil = chipStil(colorHex);
  return (
    <Chip className="ka-chip" {...(stil !== undefined ? { style: stil } : {})}>
      <span className="ka-chip__code">{r.shortCode}</span>
      <span className="ka-chip__sep"> · </span>
      <span className="ka-chip__nr">{r.customerNumber}</span>
    </Chip>
  );
}

function UebersichtInhalt(
  { kunde, monitor }: { kunde: KundeAkteDaten; monitor: MonitorKartenClient },
): ReactElement {
  return (
    <div>
      <p className="ka-uebersicht__zeile">
        {kunde.industry ?? "Branche unbekannt"}
        {kunde.city !== null && kunde.city !== undefined && kunde.city !== "" ? ` · ${kunde.city}` : ""}
      </p>
      <p className="ka-uebersicht__zeile">Letzte Aktivität: {relativeZeit(kunde.lastActivityAt)}</p>
      <p className="ka-uebersicht__zeile">{zuordnungsSatz(kunde.relations)}</p>
      {/* Die Monitor-Karten laden GETRENNT vom Rest (Spec): Was hier steht, ist bereits
          gezeichnet, bevor der erste Abruf zurück ist — ein klemmender Monitor hält die
          Kundenseite unter keinen Umständen auf. Der `key` am Kunden zwingt die Karten
          beim Kundenwechsel zum Neuaufbau, statt einen alten Zustand weiterzutragen. */}
      <KundeMonitorKarten key={kunde.id} client={monitor} customerId={kunde.id} />
    </div>
  );
}

/** Id des Panels zu einem Reiter. Sie ist zugleich die Kennung des `me-reiter` (`fuer`):
 *  Das Register meldet sie in `me-reiter-gewaehlt` und erwartet sie in `aktiv`. Das Präfix
 *  hält die Id im Dokument eindeutig — „stammdaten" allein wäre als globale Id zu kurz. */
function panelId(id: AktenTabId): string {
  return `ka-panel-${id}`;
}

export function KundenAkte(p: KundenAkteProps): ReactElement {
  const companies = p.companies ?? [];
  // Nicht-null-Behauptung: AKTEN_TABS ist ein feststehendes, nie leeres Literal — [0]
  // existiert immer, auch wenn noUncheckedIndexedAccess das dem Indexzugriff nicht ansieht.
  const aktiv = AKTEN_TABS.find((t) => t.id === p.activeTab) ?? AKTEN_TABS[0]!;

  // Nur die Unternehmen, bei denen dieser Kunde eine Beziehung hat — die einzigen, unter denen
  // Angebote/Aufträge angelegt und deren bestehende Kürzel aufgelöst werden. `kunde.relations`
  // trägt die shortCodes, die globale `companies`-Liste (aus der Sitzung) trägt id/Name/Kürzel;
  // der shortCode ist der Verbindungsschlüssel — dasselbe Muster wie bei den Chips im Kopf.
  const kundenUnternehmen = companies.filter(
    (c) => p.kunde.relations.some((r) => r.shortCode === c.shortCode));

  // Fremder Kunde? Nur entscheidbar, wenn das Arbeitsunternehmen bekannt ist — sonst lieber
  // gar keine Markierung als eine falsche.
  const arbeitskuerzel = companies.find((c) => c.id === p.arbeitsunternehmenId)?.shortCode;
  const fremdeKuerzel = arbeitskuerzel === undefined
    || p.kunde.relations.some((r) => r.shortCode === arbeitskuerzel)
    ? []
    : p.kunde.relations.map((r) => r.shortCode);

  // Die Angebot→Auftrag-Naht (Ruling P3A-B): Klickt der Angebote-Reiter „Auftrag anlegen", legt
  // er hier eine Vorbelegung ab und die Akte wechselt auf den Aufträge-Reiter, der sie beim
  // Mounten aufnimmt. KundenAkte hält den Zustand — der Angebote-Reiter erzeugt keinen Auftrag.
  const [vorbelegung, setVorbelegung] = useState<Vorbelegung | null>(null);

  // Die Auftrag→Rechnung-Naht (Paket 3b-2): Der Aufträge-Reiter LEITET die Rechnung ab und
  // meldet den Entwurf hier herauf; die Akte merkt sich seine Kennung und wechselt in den
  // Rechnungen-Reiter, der ihn beim Laden aufschlägt. Die Akte hält den Zustand — dieselbe
  // Aufgabenteilung wie bei der Angebot→Auftrag-Naht darüber.
  const [neueRechnungId, setNeueRechnungId] = useState<string | null>(null);

  function auftragAusAngebot(a: Angebot): void {
    setVorbelegung({
      offerId: a.id,
      companyId: a.companyId,
      title: a.title,
      netAmount: a.netAmount,
      contactId: a.contactId,
      responsibleUserId: a.responsibleUserId,
      periodBasis: a.periodBasis,
    });
    p.onTab("auftraege");
  }

  // Die Vorbelegung verfällt, sobald der Aufträge-Reiter verlassen wird. KundeAuftraege liest sie
  // nur beim Mounten; bliebe sie stehen, öffnete ein späterer Rücksprung auf den Reiter erneut
  // die vorbelegte Anlage. setVorbelegung(null) ist unschädlich, wenn ohnehin schon null.
  useEffect(() => {
    if (p.activeTab !== "auftraege") setVorbelegung(null);
  }, [p.activeTab]);

  function rechnungAusAuftrag(r: Rechnung): void {
    setNeueRechnungId(r.id);
    p.onTab("rechnungen");
  }

  // Und dieselbe Verfallsregel: Der Rechnungen-Reiter liest die Kennung beim Laden. Bliebe
  // sie stehen, schlüge ein späterer Rücksprung auf den Reiter erneut denselben Entwurf auf.
  useEffect(() => {
    if (p.activeTab !== "rechnungen") setNeueRechnungId(null);
  }, [p.activeTab]);

  // Reiter → Akte: Das Register setzt `aktiv` beim Klick und bei der Tastatur selbst und
  // meldet die Kennung (= Panel-Id). Pfeiltasten, Home/Ende, Roving tabindex und der Überlauf
  // in „…" liegen seit ME-Etappe 2 (Aufgabe 4b) im Element, nicht mehr hier.
  function reiterGewaehlt(kennung: string): void {
    const tab = AKTEN_TABS.find((t) => panelId(t.id) === kennung);
    if (tab !== undefined) p.onTab(tab.id);
  }

  const hatLogo = p.kunde.logoFile !== null && p.kunde.logoFile !== undefined;

  return (
    <div className="ka">
      <header className="ka__kopf">
        <Steckbrief
          art="spalte"
          // Die Akte ist die Hauptsache der Seite: Ihr Titel ist das h1 (Review A4 W1,
          // me-design fc7f4c8 `ueberschrift`). Vorgabe des Elements wäre h2.
          ueberschrift={1}
          titel={p.kunde.companyName}
          // Nur zur Wiedererkennung, klein. Lädt das Logo nicht (404, Datei weg), wird
          // das Bild ausgeblendet — KEIN Platzhalter (Spec). Aus der eigenen Domäne
          // ausgeliefert und dort mit Riegel-2-Kopfzeilen versehen (routes/logo.ts).
          // `iconArt="bild"` (Entscheid Dirk 24, me-design 3311f91): Kasten ohne Fläche,
          // das Logo auf Kastenhöhe eingepasst, höchstens doppelt so breit — die Maße bringt
          // das Element mit; `ka__logo` ist nur noch Kennzeichen.
          {...(hatLogo ? { iconArt: "bild" as const } : {})}
          icon={hatLogo && (
            <img
              className="ka__logo"
              src={`/api/customers/${p.kunde.id}/logo`}
              alt=""
              onError={(e) => { e.currentTarget.hidden = true; }}
            />
          )}
          // Fremd-Unternehmen-Chips: Nur das Warnbild „dieser Kunde gehört einem
          // anderen Arbeitsunternehmen". Die Kennwerte des Kunden je Unternehmen
          // (Kürzel+Nummer, `KundenChip`) stehen NICHT hier, sondern im Slot `werte`
          // unter der Anschrift (Aufgabe 4b): Die farbigen Firmen-Pillen sind ein
          // anderes Konzept als der `status`-Slot (Einzel-Chip/Statusmenü laut
          // me-steckbrief.js).
          status={fremdeKuerzel.length > 0 ? fremdeKuerzel.map((k) => (
            <Chip key={k} className="ka__fremd" aria-label={`Unternehmen ${k}`}>{k}</Chip>
          )) : undefined}
          // Die HAUPTANSCHRIFT (Adressen Etappe 1), die „Teil von <Eltern>"-Zeile
          // (Adressen Etappe 2) und die Kundennummern-Chips (Aufgabe 4b) stehen im Slot
          // `werte`: Alle sind Kennwerte des Kunden, die zu jedem Reiter gehören und
          // nicht zu einem. Ohne alle drei bleibt
          // der Slot leer (kein Container im Shadow DOM) statt eine leere Fläche zu
          // zeigen — `undefined` statt eines leeren Fragments.
          werte={p.kunde.hauptanschriftZeilen.length > 0
            || (p.kunde.parent !== null && p.kunde.parent !== undefined)
            || p.kunde.relations.length > 0 ? (
            <>
              {/* Sie kommt FERTIG als Zeilen vom Server (abgeleitet aus der
                  Hauptadresse, siehe adressAbleitung.ts); die Akte setzt sie nicht aus
                  flachen Feldern zusammen, sonst stünde hier eine zweite, alternde
                  Fassung des Blockbaus. Ohne Adresse steht gar nichts da — keine Lücke
                  mit Bindestrichen. */}
              {p.kunde.hauptanschriftZeilen.length > 0 && (
                <address className="ka__anschrift" role="group" aria-label="Anschrift">
                  {p.kunde.hauptanschriftZeilen.map((z, i) => (
                    <span key={`anschrift-${i}`} className="ka__anschriftzeile">{z}</span>
                  ))}
                </address>
              )}
              {/* Wo dieser Kunde im Konzern hängt, gehört zu jedem Reiter und nicht
                  hinter einen. Bei einem nicht einsehbaren Elternteil steht nur die
                  TATSACHE da (Festlegung F1) — ohne Namen und ohne Sprung: Ein Ziel,
                  das mit 404 antwortete, wäre schlimmer als ein Text. Ohne Elternteil
                  steht hier gar nichts. */}
              {p.kunde.parent !== null && p.kunde.parent !== undefined && (
                <div className="ka__eltern">
                  {p.kunde.parent.sichtbar && p.kunde.parent.id !== null ? (
                    <button type="button" className="ka__eltern-verweis"
                      aria-label={`Teil von ${p.kunde.parent.companyName}, anzeigen`}
                      onClick={() => p.onKunde(p.kunde.parent!.id!)}>
                      Teil von {p.kunde.parent.companyName}
                    </button>
                  ) : (
                    <span className="ka__eltern-verdeckt">
                      Teil eines übergeordneten Kunden — nicht einsehbar
                    </span>
                  )}
                  {p.kunde.parent.archiviert && (
                    <span className="ka__eltern-marke">Eltern archiviert</span>)}
                </div>
              )}
              {/* Kürzel + Kundennummer je Unternehmensbeziehung (Aufgabe 4b): Kennwerte des
                  Kunden wie Anschrift und Konzernzeile, darum im selben Slot darunter. Eine
                  einzelne `customerNumber` am Kunden gibt es nicht — sie steht je Beziehung
                  in `relations[]`. */}
              {p.kunde.relations.length > 0 && (
                <div className="ka__chips" role="group" aria-label="Kundennummern">
                  {p.kunde.relations.map((r) => (
                    <KundenChip
                      key={r.shortCode}
                      r={r}
                      colorHex={companies.find((c) => c.shortCode === r.shortCode)?.colorHex}
                    />
                  ))}
                </div>
              )}
            </>
          ) : undefined}
        />
      </header>

      {/* Die Reiterleiste (ME-Etappe 2, Aufgabe 4b): `me-register` mit einem `me-reiter` je
          Bereich, Reiter in Inhaltsbreite, was nicht passt, liegt hinter „…". Der aktive
          Reiter liegt auf der Unterlinie der Leiste und trägt --grund — das Panel darunter
          trägt dieselbe Fläche, beide bilden eine geschlossene Fläche (Dirk, 08.10.2026).
          `role="tablist"` und `role="tab"` stehen im Shadow DOM der Elemente; `fuer` setzt
          dort `aria-controls`, das über die Schattengrenze aber nicht auf das Panel zeigt —
          das Panel trägt deshalb seinen Namen selbst (`aria-label`). */}
      {/* `aria-label` am Host spiegelt me-register (ab 7bc0431) auf seine tablist im Shadow
          DOM — der zugängliche Name der Leiste wie vor Aufgabe 4b. */}
      <Register className="ka__register" aria-label="Bereiche der Kundenakte"
        aktiv={panelId(aktiv.id)}
        onReiterGewaehlt={(d) => reiterGewaehlt(d.id)}>
        {AKTEN_TABS.map((t) => (
          <Reiter key={t.id} fuer={panelId(t.id)} beschriftung={t.label} />
        ))}
      </Register>

      {/* Der Tab-Wechsel tauscht ausschließlich diesen einen Bereich — Kopf und Tab-Leiste
          bleiben stehen. "Der Kunde ist eine Ebene, kein Ziel." */}
      <div role="tabpanel" id={panelId(aktiv.id)} className="ka__panel" aria-label={aktiv.label}>
        {/* Welche Tabs Inhalt haben, sagt ausschliesslich dieser Zweig — je gebauter Bereich
            eine eigene Bedingung, alle übrigen fallen auf den Platzhalter mit ihrer
            Paketnummer. Bewusst KEINE einzelne „bis Paket N"-Grenze mehr (früher
            FERTIGE_PAKETE): Paket 4 (Notizen/Dateien) ist fertig, während Paket 3
            (Angebote/Aufträge/Wichtiges) noch aussteht — eine fortlaufende Zahl könnte
            „1, 2 und 4 gebaut, 3 offen" nicht ausdrücken und liesse die Paket-3-Tabs
            fälschlich Inhalt statt ihres Platzhalters zeigen. */}
        {aktiv.id === "stammdaten"
          ? <KundeStammdaten kunde={p.kunde} companies={companies} {...p.stammdaten} />
          : aktiv.id === "adressen"
            // `key` am Kunden: Der Baustein hält seinen eigenen Zustand (Liste, Panel), und
            // beim Kundenwechsel soll nichts davon überleben — dieselbe Vorsorge wie bei
            // den Monitor-Karten der Übersicht.
            ? <AdressListe key={p.kunde.id} client={p.adressen.client}
                besitzer="customers" ownerId={p.kunde.id} titel="Adressen"
                namensVorschlag={p.kunde.companyName}
                onKunde={p.onKunde}
                onGeaendert={p.adressen.onNeuLaden} />
          : aktiv.id === "konzern"
            // `key` am Kunden — aus demselben Grund wie beim Adressen-Reiter darüber, und
            // hier noch dringlicher: Dieser Reiter führt SELBST in eine andere Akte.
            ? <KundeKonzern key={p.kunde.id} client={p.konzern.client} kunde={p.kunde}
                onKunde={p.onKunde} onAktualisiert={p.konzern.onAktualisiert}
                companies={companies} />
          : aktiv.id === "ansprechpartner"
            ? <KundeAnsprechpartner kundeId={p.kunde.id} onKunde={p.onKunde}
                {...p.ansprechpartner} />
            : aktiv.id === "angebote"
              // `kundeName` allein für die Platzhalter-Vorschau der Angebotstexte
              // (#{kunde.name}) — der Reiter lädt den Kunden nicht selbst.
              ? <KundeAngebote kundeId={p.kunde.id} client={p.angebote.client}
                  companies={kundenUnternehmen} onAuftragAusAngebot={auftragAusAngebot}
                  oeffneAngebotId={p.oeffneVorgangId ?? null}
                  arbeitsunternehmenId={p.arbeitsunternehmenId ?? null}
                  kundeName={p.kunde.companyName} />
              : aktiv.id === "auftraege"
                // `kundeName` auch hier allein für die Platzhalter-Vorschau der
                // Auftragstexte (#{kunde.name}) — wie am Angebote-Reiter.
                ? <KundeAuftraege kundeId={p.kunde.id} client={p.auftraege.client}
                    companies={kundenUnternehmen} vorbelegung={vorbelegung}
                    onRechnungAusAuftrag={rechnungAusAuftrag}
                    oeffneAuftragId={p.oeffneVorgangId ?? null}
                    kundeName={p.kunde.companyName}
                    istAdmin={p.auftraege.istAdmin === true} />
                : aktiv.id === "rechnungen"
                  // `kundeName` auch hier allein für die Platzhalter-Vorschau der
                  // Rechnungstexte (#{kunde.name}).
                  ? <KundeRechnungen kundeId={p.kunde.id} client={p.rechnungen.client}
                      companies={kundenUnternehmen} oeffneRechnungId={neueRechnungId}
                      kundeName={p.kunde.companyName} />
                  : aktiv.id === "wichtiges"
                    ? <KundeWichtiges kundeId={p.kunde.id} {...p.wichtiges} />
                    : aktiv.id === "notizen"
                      ? <KundeNotizen kundeId={p.kunde.id} {...p.notizen} />
                      : aktiv.id === "dateien"
                        ? <KundeDateien kundeId={p.kunde.id} {...p.dateien} />
                        : aktiv.id === "uebersicht"
                          ? <UebersichtInhalt kunde={p.kunde}
                              monitor={p.monitor.client} />
                          : <p className="ka__platzhalter">Dieser Bereich kommt mit Paket {aktiv.paket}.</p>}
      </div>
    </div>
  );
}
